import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Feed from "@/pages/Feed";
import Explore from "@/pages/Explore";
import Profile from "@/pages/Profile";
import Upload from "@/pages/Upload";
import Communities from "@/pages/Communities";
import CommunityPage from "@/pages/Community";
import ThreadPage from "@/pages/Thread";
import AuthPage from "@/pages/Auth";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import Dashboard from "@/pages/Dashboard";
import Broadcast from "@/pages/Broadcast";
import Subscriptions from "@/pages/Subscriptions";
import Messages from "@/pages/Messages";
import RealtimeBridge from "@/components/Realtime";

function RequireAuth({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="p-10 text-slate-400" data-testid="auth-loading">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function MeRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  return <Navigate to={user ? `/u/${user.username}` : "/login"} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <RealtimeBridge />
        <BrowserRouter>
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Feed />} />
              <Route path="/explore" element={<Explore />} />
              <Route path="/u/:username" element={<Profile />} />
              <Route path="/me" element={<MeRedirect />} />
              <Route path="/upload" element={<RequireAuth><Upload /></RequireAuth>} />
              <Route path="/c" element={<Communities />} />
              <Route path="/c/:slug" element={<CommunityPage />} />
              <Route path="/c/:slug/t/:threadId" element={<ThreadPage />} />
              <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
              <Route path="/broadcast" element={<RequireAuth><Broadcast /></RequireAuth>} />
              <Route path="/subscriptions" element={<RequireAuth><Subscriptions /></RequireAuth>} />
              <Route path="/messages" element={<RequireAuth><Messages /></RequireAuth>} />
              <Route path="/messages/:username" element={<RequireAuth><Messages /></RequireAuth>} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="*" element={
                <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-center" data-testid="not-found-page">
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-gray-500">404</p>
                  <h1 className="text-xl font-semibold tracking-tight">This page doesn't exist</h1>
                  <a href="/" className="text-sm text-[#FF2E4D] hover:underline" data-testid="not-found-home-link">Back to feed</a>
                </div>
              } />
            </Route>
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/signup" element={<AuthPage mode="signup" />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          theme="dark"
          position="top-center"
          toastOptions={{ style: { background: "#121418", border: "1px solid #2A2E37", borderRadius: "4px", color: "#F3F4F6" } }}
        />
      </AuthProvider>
    </div>
  );
}

export default App;
